import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useDaggerheartData, useHomebrewOverride } from '../../context/DataContext';
import useHomebrewLoader from '../../hooks/useHomebrewLoader';
import { migrateCharacter, CURRENT_SCHEMA_VERSION } from '../../services/characterMigrationService';
import toast from 'react-hot-toast';
import LevelTimeline from './LevelTimeline';
import AutoSaveIndicator from './AutoSaveIndicator';
import LevelOneEditor from './LevelOneEditor';
import LevelAdvancementEditor from './LevelAdvancementEditor';
import { Loader2, ScrollText } from 'lucide-react';
import IconDaggerheart from '../common/IconDaggerheart';
import { cn } from '../../lib/utils';

/**
 * Resolve a display name to an SRD ID by searching allData entries.
 * Case-insensitive match on the name field.
 */
function findSrdId(collection, displayName) {
  if (!collection || !displayName) return '';
  const needle = displayName.toLowerCase().replace(/^homebrew:/i, '');
  for (const [id, data] of Object.entries(collection)) {
    const name = (data.name || '').toLowerCase();
    if (name === needle) return id;
  }
  return '';
}

/**
 * Check sessionStorage for a Brewmaster conversion draft and pre-fill
 * a new character from it. Consumes the draft (removes from storage).
 */
function applyConversionDraft(character, allData) {
  try {
    const raw = sessionStorage.getItem('chag_conversion_draft');
    if (!raw) return character;

    const draft = JSON.parse(raw);
    sessionStorage.removeItem('chag_conversion_draft');

    if (!draft || typeof draft !== 'object') return character;

    const prefilled = { ...character };

    // Name
    if (draft.characterName) {
      prefilled.name = draft.characterName;
    }

    // Class (resolve display name to SRD ID)
    if (draft.class?.selected) {
      const classId = findSrdId(allData?.classes, draft.class.selected);
      if (classId) {
        prefilled.class = classId;
        prefilled.domains = allData.classes[classId]?.domains || [];
      }
    }

    // Subclass
    if (draft.subclass?.selected && !draft.subclass.selected.startsWith('homebrew:')) {
      const subId = findSrdId(allData?.subclasses, draft.subclass.selected);
      if (subId) prefilled.subclass = subId;
    }

    // Ancestry (array of SRD IDs)
    const ancestryIds = [];
    if (draft.ancestry?.primary) {
      const id = findSrdId(allData?.ancestries, draft.ancestry.primary);
      if (id) ancestryIds.push(id);
    }
    if (draft.ancestry?.secondary) {
      const id = findSrdId(allData?.ancestries, draft.ancestry.secondary);
      if (id) ancestryIds.push(id);
    }
    if (ancestryIds.length > 0) prefilled.ancestry = ancestryIds;

    // Community
    if (draft.community?.selected) {
      const id = findSrdId(allData?.communities, draft.community.selected);
      if (id) prefilled.community = id;
    }

    // Traits
    if (draft.traits && typeof draft.traits === 'object') {
      prefilled.traits = { ...character.traits, ...draft.traits };
    }

    // Equipment
    if (draft.equipment) {
      prefilled.equipment = {
        weapon1: draft.equipment.weapon1 || '',
        weapon2: draft.equipment.weapon2 || '',
        armor: draft.equipment.armor || '',
      };
    }

    // Domain cards (loadout)
    if (Array.isArray(draft.domainCards?.loadout)) {
      prefilled.loadout = draft.domainCards.loadout.slice(0, 2);
    }

    // Experiences and background
    if (Array.isArray(draft.experiences)) {
      prefilled.experiences = draft.experiences;
    }
    if (draft.background) {
      prefilled.background = draft.background;
    }

    // Store conversion plan for level-up guidance
    if (draft.conversionPlan) {
      prefilled.conversionPlan = {
        source: draft.conversionPlan.source || 'brewmaster',
        sourceLevel: draft.targetLevel?.sourceLevel || null,
        targetLevel: draft.targetLevel?.dhLevel || 1,
        recommendations: draft.conversionPlan.recommendations || {},
      };
    }

    return prefilled;
  } catch {
    return character;
  }
}

/**
 * CharacterBuilder - Unified character creation/editing/leveling interface
 *
 * ONE experience for:
 * - Creating new characters (building level 1)
 * - Leveling up (building next level)
 * - Editing (modifying any past level)
 *
 * Layout:
 * - Left/Top: Level timeline (select which level to edit)
 * - Right/Bottom: Editor for selected level
 * - Auto-save with visual feedback
 *
 * @param {string} characterId - Character ID from URL params (or 'new' for creation)
 */
function CharacterBuilder({ onCharacterForge }) {
  const { characterId } = useParams();
  const { user } = useAuth();
  const allData = useDaggerheartData();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Load homebrew content so homebrew items appear in selection lists
  const setHomebrewCardOverrides = useHomebrewOverride();
  const { homebrewCards, loaded: homebrewLoaded } = useHomebrewLoader(user?.uid);
  useEffect(() => {
    if (homebrewLoaded && Object.keys(homebrewCards).length > 0) {
      setHomebrewCardOverrides(homebrewCards);
    }
  }, [homebrewCards, homebrewLoaded, setHomebrewCardOverrides]);

  // Core state
  const [character, setCharacter] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Completion status for each level
  const [levelCompletionStatus, setLevelCompletionStatus] = useState({});

  // Load character on mount
  useEffect(() => {
    loadCharacter();
  }, [characterId]);

  /**
   * Load character from Firestore (or initialize new character)
   */
  async function loadCharacter() {
    setLoading(true);

    try {
      if (characterId === 'new') {
        // Initialize new character
        const newCharacter = {
          id: 'new',
          userId: user.uid,
          level: 1,
          name: '',
          class: '',
          subclass: '',
          ancestry: [],
          community: '',
          traits: {
            Agility: 0,
            Strength: 0,
            Finesse: 0,
            Instinct: 0,
            Presence: 0,
            Knowledge: 0
          },
          equipment: {
            weapon1: '',
            weapon2: '',
            armor: ''
          },
          loadout: [],
          vault: [],
          experiences: [],
          background: '',
          pronouns: '',
          campaignFrame: 'core',
          // NEW: Level history tracking
          levelHistory: {
            1: {
              choices: {},
              timestamp: null
            }
          },
          // NEW: SRD compliance fields
          traitMarks: {},
          permanentSlots: {
            hp: [],
            stress: []
          },
          schemaVersion: CURRENT_SCHEMA_VERSION // Track schema version for migrations
        };

        // Check for Brewmaster conversion draft
        const prefilled = applyConversionDraft(newCharacter, allData);

        setCharacter(prefilled);
        setSelectedLevel(1);
        setLevelCompletionStatus({ 1: false }); // Level 1 incomplete
      } else {
        // Load existing character
        const characterRef = doc(db, 'characters', characterId);
        const characterSnap = await getDoc(characterRef);

        if (!characterSnap.exists()) {
          toast.error('Character not found');
          navigate('/');
          return;
        }

        const characterData = { id: characterSnap.id, ...characterSnap.data() };

        // Migrate old characters to new schema if needed
        const { character: migratedCharacter, needsSave, migrationsRun } = migrateCharacter(characterData, allData);

        if (needsSave && migrationsRun.length > 0) {
          console.log('Migrations applied in CharacterBuilder:', migrationsRun);
          // Save migrated character to Firestore
          try {
            const charRef = doc(db, 'characters', characterId);
            await updateDoc(charRef, { ...migratedCharacter, updatedAt: serverTimestamp() });
          } catch (err) {
            console.error('Failed to save migrated character:', err);
          }
        }

        // Clean any undefined values from loaded character
        const cleanedCharacter = deepClean(migratedCharacter);
        setCharacter(cleanedCharacter);

        // Determine initial selected level (from query param or default to current)
        const levelParam = parseInt(searchParams.get('level'));
        const initialLevel = levelParam || migratedCharacter.level;
        setSelectedLevel(initialLevel);

        // Calculate completion status for all levels
        const completionStatus = calculateCompletionStatus(migratedCharacter);
        setLevelCompletionStatus(completionStatus);
      }
    } catch (error) {
      console.error('Error loading character:', error);
      toast.error('Failed to load character');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }

  /**
   * Calculate which levels are complete (all required choices made)
   */
  function calculateCompletionStatus(character) {
    const status = {};

    for (let lvl = 1; lvl <= character.level; lvl++) {
      if (lvl === 1) {
        // Level 1 requires: class, ancestry, traits assigned, 2 domain cards
        const level1Complete =
          character.class &&
          character.ancestry?.length > 0 &&
          character.loadout?.length >= 2 &&
          character.traits &&
          Object.values(character.traits).reduce((a, b) => a + b, 0) !== 0;

        status[1] = level1Complete;
      } else {
        // Levels 2+ require: advancement choices made
        const levelHistory = character.levelHistory?.[lvl];
        const hasChoices = levelHistory && levelHistory.choices &&
          (levelHistory.choices.advancements?.length > 0 || levelHistory.legacy);

        status[lvl] = hasChoices !== false;
      }
    }

    return status;
  }

  /**
   * Handle character updates (auto-save)
   */
  async function handleUpdate(updates) {
    try {
      // Deep clean updates before merging to prevent undefined values
      const cleanedUpdates = deepClean(updates);

      const updatedCharacter = { ...character, ...cleanedUpdates };

      // Deep clean the entire merged character (in case existing character has undefined values)
      const fullyCleanedCharacter = deepClean(updatedCharacter);

      setCharacter(fullyCleanedCharacter);

      // Auto-save to Firestore if not a new character
      if (character.id !== 'new') {
        setSaving(true);
        await saveToFirestore(fullyCleanedCharacter);
      }
    } catch (error) {
      console.error('Error updating character:', error);
      toast.error('Failed to save changes');
    }
  }

  /**
   * Deep clean: Remove all undefined values recursively
   */
  function deepClean(obj) {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => deepClean(item));
    }

    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = deepClean(value);
      }
    }
    return cleaned;
  }

  /**
   * Find paths to undefined values in an object (for debugging)
   */
  function findUndefinedPaths(obj, path = '') {
    const paths = [];

    if (obj === undefined) {
      return [path];
    }

    if (obj === null || typeof obj !== 'object') {
      return paths;
    }

    if (Array.isArray(obj)) {
      obj.forEach((item, index) => {
        paths.push(...findUndefinedPaths(item, `${path}[${index}]`));
      });
      return paths;
    }

    for (const [key, value] of Object.entries(obj)) {
      const newPath = path ? `${path}.${key}` : key;
      if (value === undefined) {
        paths.push(newPath);
      } else if (typeof value === 'object') {
        paths.push(...findUndefinedPaths(value, newPath));
      }
    }

    return paths;
  }

  /**
   * Save character to Firestore
   */
  async function saveToFirestore(characterData) {
    try {
      const characterRef = doc(db, 'characters', characterData.id);
      const { id, ...dataToSave } = characterData;

      // Deep clean to remove all undefined values (Firestore doesn't accept undefined)
      const cleanedData = deepClean(dataToSave);

      // Check for undefined values in cleaned data
      const undefinedPaths = findUndefinedPaths(cleanedData);
      if (undefinedPaths.length > 0) {
        console.error('❌ Found undefined values at paths:', undefinedPaths);
        throw new Error(`Cannot save: undefined values found at: ${undefinedPaths.join(', ')}`);
      }

      // Prepare the data object
      const dataToSend = {
        ...cleanedData,
        updatedAt: serverTimestamp()
      };

      // Final check after spread operator
      const finalPaths = findUndefinedPaths(dataToSend);
      if (finalPaths.length > 0) {
        console.error('❌❌❌ UNDEFINED VALUES FOUND AFTER SPREAD!');
        console.error('Paths:', finalPaths);
        console.error('Full object:', dataToSend);
        throw new Error(`Undefined after spread: ${finalPaths.join(', ')}`);
      }

      // Verify data can be stringified (Firestore requirement check)
      try {
        JSON.stringify(dataToSend);
      } catch (e) {
        console.error('❌ Could not stringify data:', e);
        throw new Error('Data contains non-serializable values');
      }

      // Force JSON round-trip to remove any non-serializable values
      const jsonSafe = JSON.parse(JSON.stringify(dataToSend, (key, value) => {
        // Skip undefined values during stringify
        return value === undefined ? null : value;
      }));

      // Add back serverTimestamp (it was lost in JSON round-trip)
      jsonSafe.updatedAt = serverTimestamp();

      // Use setDoc with merge instead of updateDoc - more forgiving with nested objects
      await setDoc(characterRef, jsonSafe, { merge: true });

      setSaving(false);

      // Recalculate completion status
      const completionStatus = calculateCompletionStatus(characterData);
      setLevelCompletionStatus(completionStatus);

      return true;
    } catch (error) {
      setSaving(false);
      throw error;
    }
  }

  /**
   * Save Level 1 history (called when navigating away from Level 1)
   */
  function saveLevelOneHistory() {
    // Skip if Level 1 history already exists
    if (character.levelHistory?.[1]) {
      return;
    }

    const levelHistory = character.levelHistory || {};
    levelHistory[1] = {
      choices: {
        campaignFrame: character.campaignFrame,
        class: character.class,
        subclass: character.subclass,
        ancestry: character.ancestry,
        community: character.community,
        traits: character.traits,
        equipment: character.equipment,
        startingItems: character.startingItems,
        loadout: character.loadout?.slice(0, 2), // First 2 cards
        name: character.name,
        pronouns: character.pronouns,
        experiences: character.experiences,
        background: character.background
      },
      timestamp: Date.now(),
      appliedUpdates: {} // Level 1 doesn't have discrete "updates"
    };

    handleUpdate({ levelHistory });
  }

  /**
   * Handle level selection
   */
  function handleLevelSelect(level) {
    // Check if trying to advance beyond current level
    if (level > character.level && level === character.level + 1) {
      // Advancing to next level - check if current level is complete
      if (levelCompletionStatus[character.level] === false) {
        toast.error(`Complete Level ${character.level} before advancing`);
        return;
      }
    }

    // If navigating away from Level 1, save Level 1 history
    if (selectedLevel === 1 && level !== 1 && levelCompletionStatus[1]) {
      saveLevelOneHistory();
    }

    setSelectedLevel(level);

    // Update URL without navigation
    const newUrl = `/characters/${character.id}/build?level=${level}`;
    window.history.replaceState(null, '', newUrl);
  }

  /**
   * Handle level completion (called when user completes a level-up)
   * Navigates back to character sheet for level-ups, stays in builder for edits
   */
  function handleLevelComplete(completedLevel) {
    // Only navigate away if completing the NEXT level (not editing existing levels)
    if (completedLevel === character.level) {
      toast.success(`Level ${completedLevel} complete! Returning to character sheet...`, {
        duration: 3000
      });

      // Wait a moment for the toast to be visible, then navigate
      setTimeout(() => {
        navigate(`/character/${character.id}`);
      }, 1500);
    }
    // Otherwise, user is editing an existing level - stay in builder
  }

  /**
   * Revert character to a previous level
   * Deletes all progress from levels > targetLevel
   */
  async function handleRevert(targetLevel) {
    if (targetLevel >= character.level) {
      toast.error('Cannot revert to current or future level');
      return;
    }

    try {
      const levelHistory = { ...character.levelHistory };

      // Collect domain cards to remove (from deleted levels)
      const cardsToRemove = new Set();
      for (let lvl = targetLevel + 1; lvl <= character.level; lvl++) {
        const lvlChoices = levelHistory[lvl]?.choices;
        if (lvlChoices) {
          // Remove mandatory domain card
          if (lvlChoices.mandatoryDomainCard) {
            cardsToRemove.add(lvlChoices.mandatoryDomainCard);
          }
          // Remove advancement domain cards
          if (lvlChoices.selectedDomainCards?.length > 0) {
            lvlChoices.selectedDomainCards.forEach(card => cardsToRemove.add(card));
          }
          // Remove bonus domain cards (School of Knowledge)
          if (lvlChoices.bonusDomainCards?.length > 0) {
            lvlChoices.bonusDomainCards.forEach(card => cardsToRemove.add(card));
          }
        }

        // Delete level history
        delete levelHistory[lvl];
      }

      // Remove cards from loadout and vault
      const updatedLoadout = (character.loadout || []).filter(cardId => !cardsToRemove.has(cardId));
      const updatedVault = (character.vault || []).filter(cardId => !cardsToRemove.has(cardId));

      // Update character
      const updates = {
        level: targetLevel,
        levelHistory,
        loadout: updatedLoadout,
        vault: updatedVault
      };

      await handleUpdate(updates);

      // Navigate to the target level
      setSelectedLevel(targetLevel);
      const newUrl = `/characters/${character.id}/build?level=${targetLevel}`;
      window.history.replaceState(null, '', newUrl);

      const deletedLevels = character.level - targetLevel;
      const deletedCards = cardsToRemove.size;
      toast.success(
        `Reverted to Level ${targetLevel}. Deleted ${deletedLevels} level${deletedLevels > 1 ? 's' : ''} and ${deletedCards} domain card${deletedCards !== 1 ? 's' : ''}.`,
        { duration: 5000 }
      );
    } catch (error) {
      console.error('Error reverting level:', error);
      toast.error('Failed to revert level');
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading character...</p>
        </div>
      </div>
    );
  }

  // Error state (shouldn't happen, but defensive)
  if (!character) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">Character not found</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background min-h-screen text-gray-100">
      {/* Header Banner - Fixed at top (matching TabbedCharacterSheet) */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-surface-1 p-4 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <IconDaggerheart className="w-8 h-8 text-red-400" />
          <h1 className="text-2xl font-bold text-gray-100 tracking-wider font-serif">
            Chaggerheart
          </h1>
        </div>

        {/* Right-side actions */}
        <div className="flex items-center gap-3">
          {/* Auto-save indicator */}
          <AutoSaveIndicator saving={saving} />

          {/* View Character Sheet Icon (only for existing characters) */}
          {characterId !== 'new' && (
            <button
              onClick={() => navigate(`/character/${characterId}`)}
              className="p-2 bg-gray-800 text-gray-400 rounded-md hover:text-red-400 hover:bg-red-900/30 border border-gray-600 hover:border-red-700 transition-all duration-200"
              title="View Character Sheet"
            >
              <ScrollText className="w-5 h-5" />
            </button>
          )}

          {/* Main Menu Button */}
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-gray-800 text-gray-100 rounded-md hover:text-red-400 hover:bg-red-900/30 border border-gray-600 hover:border-red-700 transition-all duration-200 font-medium"
          >
            Main Menu
          </button>
        </div>
      </header>

      {/* Character Builder Container - Padding for fixed header */}
      <div className="min-h-screen pt-[73px]">
        {/* Level Timeline Sidebar (left on desktop, top on mobile) */}
        <LevelTimeline
          currentLevel={character.level}
          selectedLevel={selectedLevel}
          onSelect={handleLevelSelect}
          completionStatus={levelCompletionStatus}
          character={character}
          onRevert={handleRevert}
        />

        {/* Main Editor Area */}
        <main
          className={cn(
            // Mobile: bottom padding for fixed navigation
            'pb-20 lg:pb-0',
            // Desktop: left margin for sidebar (256px)
            'lg:ml-sidebar',
            // Prevent content from being cut off
            'min-w-0 max-w-full flex-1',
            // Rounded top-left corner on desktop
            'lg:rounded-tl-2xl'
          )}
        >
          {/* Level Editor Content */}
          <div className="px-4 py-6 lg:px-8 max-w-7xl mx-auto bg-background">
            {selectedLevel === 1 ? (
              <LevelOneEditor
                character={character}
                onUpdate={handleUpdate}
                onCharacterForge={onCharacterForge}
                initialSection={searchParams.get('section')}
              />
            ) : (
              <LevelAdvancementEditor
                level={selectedLevel}
                character={character}
                onUpdate={handleUpdate}
                onLevelComplete={handleLevelComplete}
              />
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default CharacterBuilder;
